import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { AuthContext } from '../App';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const auto = searchParams.get('auto') === '1';
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { refresh, switchWorkspace } = useWorkspace();
  const [invite, setInvite] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [inviter, setInviter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const autoRanRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/invites/${token}`);
        if (!mounted) return;
        setInvite(response.data?.invite);
        setWorkspace(response.data?.workspace);
        setInviter(response.data?.inviter);
      } catch (err) {
        setError(err.response?.data?.detail || 'Davet bulunamadı');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      navigate(`/?invite=${encodeURIComponent(token)}`);
      return;
    }
    setAccepting(true);
    try {
      const response = await axios.post(`${API}/invites/${token}/accept`);
      const wsId = response.data?.workspaceId;
      toast.success('Davet kabul edildi');
      await refresh();
      if (wsId) switchWorkspace(wsId);
      else navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Davet kabul edilemedi');
      toast.error(message);
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    if (autoRanRef.current) return;
    if (!auto) return;
    if (loading) return;
    if (!user) return;
    if (!invite || invite.status !== 'pending') return;
    if (user.email && invite.email && user.email.toLowerCase() !== invite.email.toLowerCase()) return;
    autoRanRef.current = true;
    handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, loading, user, invite]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="ox-surface rounded-md" style={{ padding: 32, width: 'min(440px, 96vw)', textAlign: 'center' }}>
        {loading ? (
          <div className="flex flex-col items-center gap-3 ox-text-muted">
            <Loader2 className="animate-spin" size={28} />
            <div className="text-[13px]">Davet kontrol ediliyor...</div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3">
            <XCircle size={36} style={{ color: 'var(--danger)' }} />
            <div className="text-[16px] font-semibold">Davet Geçersiz</div>
            <div className="text-[13px] ox-text-muted">{error}</div>
            <button onClick={() => navigate('/')} className="ox-btn mt-2">Anasayfaya Dön</button>
          </div>
        ) : invite?.status !== 'pending' ? (
          <div className="flex flex-col items-center gap-3">
            <XCircle size={36} style={{ color: 'var(--danger)' }} />
            <div className="text-[16px] font-semibold">Davet Artık Geçerli Değil</div>
            <div className="text-[13px] ox-text-muted">Davet durumu: {invite.status}</div>
            <button onClick={() => navigate('/')} className="ox-btn mt-2">Anasayfaya Dön</button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle size={36} style={{ color: 'var(--accent)' }} />
            <div className="text-[18px] font-semibold">Workspace Daveti</div>
            <div className="text-[13.5px] ox-text-muted" style={{ lineHeight: 1.6 }}>
              {inviter ? <strong>{inviter.name}</strong> : 'Bir yönetici'} sizi
              <br />
              <strong>{workspace?.name || 'OnxFlow Workspace'}</strong> workspace'ine
              <br />
              <strong>{invite.role}</strong> rolüyle davet etti.
            </div>
            <div className="text-[12px] ox-text-muted mt-2">Davet edilen e-posta: <strong>{invite.email}</strong></div>
            {user && user.email && user.email.toLowerCase() !== invite.email.toLowerCase() && (
              <div className="text-[12px] mt-2" style={{ color: 'var(--danger)' }}>
                Şu anda {user.email} ile oturum açmışsınız. Lütfen davet edilen e-posta ile giriş yapın.
              </div>
            )}
            {!user && (
              <div className="text-[12px] mt-2 ox-text-muted">
                Hesabın yok mu?{' '}
                <Link
                  to={`/register?invite=${encodeURIComponent(token)}`}
                  style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
                >
                  Kayıt ol
                </Link>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={() => navigate('/')} className="ox-btn">Reddet</button>
              <button onClick={handleAccept} disabled={accepting} className="ox-btn ox-btn-primary">
                {accepting ? <Loader2 className="animate-spin" size={14} /> : null}
                <span>{user ? 'Daveti Kabul Et' : 'Giriş Yap ve Kabul Et'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
